import axios from 'axios'
import { edxrUserAgentHeader } from '../api.service'
import { tagRequestID, REQUEST_ID_HEADER } from '../error/request-id'

// SSO link confirmation (owner decision Option A, 2026-10-16): when a public
// sign-in button (Google, Microsoft, Facebook...) meets an existing
// EducationXR account it isn't connected to yet, auth holds the sign-in
// until the person shows the account is theirs. These are the held sign-in
// page's calls (rapid-api-auth /api/v1/auth-provider/:id/link-confirmation,
// :id the held sign-in's token):
//
//   GET                  the page's state ({required: false} unless held)
//   POST /password       {password}
//   POST /code           {} -- email a code to the account's own address
//   POST /code/verify    {signinCode}
//   GET  /method/:id     a NAVIGATION (302 only), never a request: it starts
//                        a sign-in with a method the account already has
//
// The hold is bound to the browser whose sign-in it holds (an HttpOnly
// cookie on the auth host), so every call sends cookies (withCredentials):
// in any other browser auth answers 403 link_confirmation_other_browser.
// No Authorization header: the steps act on the token in the path, and a
// session this browser may hold is not part of it (nor is the axios
// layer's 401 handling, which would sign the browser out; these steps
// never answer 401). Every answer and refusal carries its X-Request-ID.

export const LINK_CONFIRMATION_REQUIRED = 'link_confirmation_required'

const base = token => `${process.env.REACT_APP_API_AUTH}/api/v1/auth-provider/${encodeURIComponent(token || '')}/link-confirmation`

const config = () => {
  const headers = { 'Content-Type': 'application/json' }
  const agent = edxrUserAgentHeader()
  if (agent) headers['edxr-user-agent'] = agent
  return { headers, withCredentials: true }
}

// The body, carrying the request's id (non-enumerable, as request-id.js
// marks a failure's) for a reference on a page that shows one.
const answer = response => {
  const body = response ? response.data : undefined
  const id = response && response.headers ? response.headers[REQUEST_ID_HEADER] || response.headers['X-Request-ID'] : null
  if (id && body && typeof body === 'object' && !Object.isFrozen(body)) {
    Object.defineProperty(body, 'requestID', { value: String(id), enumerable: false, configurable: true, writable: true })
  }
  return body
}

// A refusal rejects with the response ({ status, data, headers }), as the
// api-new hooks do; a network failure with axios's error.
const call = async request => {
  try {
    return answer(await request())
  } catch (error) {
    tagRequestID(error)
    throw (error && error.response) || error
  }
}

export const getLinkConfirmation = token => call(() => axios.get(base(token), config()))

export const confirmLinkWithPassword = (token, password) => call(() => axios.post(`${base(token)}/password`, { password }, config()))

export const sendLinkCode = token => call(() => axios.post(`${base(token)}/code`, {}, config()))

export const confirmLinkWithCode = (token, signinCode) => call(() => axios.post(`${base(token)}/code/verify`, { signinCode }, config()))

// Where "Continue with <method>" goes: a full-page navigation.
export const linkMethodURL = (token, identifier) => `${base(token)}/method/${encodeURIComponent(identifier || '')}`

// The held sign-in's page for a token.
export const linkConfirmationPageURL = token => {
  const url = new URL(`${process.env.REACT_APP_SITE}/open/link`)
  url.searchParams.set('token', token || '')
  return url.href
}

// Where a confirmed sign-in goes on: auth's `next` ("open/organization", a
// path of this site with no leading slash) with ?token=. Only an /open/
// page of this site is followed; anything else is the organization step.
const NEXT = /^open\/[a-z][a-z-]*$/

export const confirmedNextURL = (token, next) => {
  const path = typeof next === 'string' ? next.replace(/^\/+/, '') : ''
  const url = new URL(`${process.env.REACT_APP_SITE}/${NEXT.test(path) ? path : 'open/organization'}`)
  url.searchParams.set('token', token || '')
  return url.href
}

// Whether a sign-in step was refused because the sign-in waits for the
// person to confirm the account (403 link_confirmation_required, on the
// organization, invite, join-code, register, link and signin steps).
export const isLinkConfirmationRequired = error =>
  Boolean(error && error.data && error.data.reason === LINK_CONFIRMATION_REQUIRED)

// Sends the browser to the held sign-in's page when error says it waits
// for confirmation (replace: the refused step must not stay in history).
// True when it did.
export const sendToLinkConfirmation = (error, token, replace = url => window.location.replace(url)) => {
  if (!isLinkConfirmationRequired(error)) return false
  replace(linkConfirmationPageURL(token))
  return true
}
